import {
  DefaultError,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IYoutubeAuthorizationSettings } from './types';

export const useGetYoutubeAuthorizationSettings = (enabled = true) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IYoutubeAuthorizationSettings, DefaultError>({
    queryKey: ['admin-youtube-authorization-settings'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Youtube/AuthorizationSettings',
      });

      return responseHandler(response, {
        groupName: 'useGetYoutubeAuthorizationSettings',
      });
    },
    enabled,
    refetchOnWindowFocus: true,
  });
};

export const useGetYoutubeAuthorizationUrl = (enabled = false) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<string, DefaultError>({
    queryKey: ['admin-youtube-authorization-url'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Youtube/AuthorizationUrl',
      });

      return responseHandler(response, {
        groupName: 'useGetYoutubeAuthorizationUrl',
      });
    },
    enabled,
  });
};

export const useAuthorizeYoutubeAdmin = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss } = useQueryToast('useAuthorizeYoutubeAdmin');

  return useMutation<string, DefaultError, void>({
    async mutationFn() {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('youtubeOAuth.what'),
        })
      );

      const response = await apiClient({
        method: 'GET',
        url: 'Youtube/AuthorizationUrl',
      });

      return responseHandler(response, {
        groupName: 'useAuthorizeYoutubeAdmin',
      });
    },
    onSuccess: url => {
      dismiss();
      if (url) {
        window.location.href = url;
      }
    },
    onError: dismiss,
  });
};

export const usePostYoutubeRefreshTokenAll = () => {
  const queryClient = useQueryClient();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostYoutubeRefreshTokenAll'
  );

  return useMutation<unknown, DefaultError, void>({
    async mutationFn() {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('account.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'MagellanAccount/YouTube/RefreshToken/All',
      });

      return responseHandler(response, {
        groupName: 'usePostYoutubeRefreshTokenAll',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-youtube-authorization-settings'],
      });
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostYoutubeRefreshToken = () => {
  const queryClient = useQueryClient();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostYoutubeRefreshToken');

  return useMutation<unknown, DefaultError, string>({
    async mutationFn(id) {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('account.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `MagellanAccount/${id}/YouTube/RefreshToken`,
      });

      return responseHandler(response, {
        groupName: `usePostYoutubeRefreshToken - ${id}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-youtube-authorization-settings'],
      });
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('account.what'),
        })
      );
    },
    onError: dismiss,
  });
};
